import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import liteIcon from "../assets/icons/LiteIcon.png";
import "../css/LoginPage.css";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link as MuiLink,
} from "@mui/material";
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaEnvelope,
  FaCheckCircle,
  FaPaperPlane,
} from "react-icons/fa";
import { API_URL } from "../config";
import { toast } from "react-toastify";

// ─── Motion helpers ─────────────────────────────────────────────────────────
const EASE = [0.16, 1, 0.3, 1];
const SPRING = { type: "spring", stiffness: 420, damping: 30 };

const MotionBox = motion(Box);


// ─── Particle component ─────────────────────────────────────────────────────
const Particle = ({ delay, duration, x, y, size, color }) => (
  <MotionBox
    initial={{ opacity: 0, y: 0, x: 0, scale: 0 }}
    animate={{
      opacity: [0, 0.7, 0],
      y: [0, -120],
      x: [0, (Math.random() - 0.5) * 60],
      scale: [0, 1, 0],
    }}
    transition={{
      duration,
      delay,
      repeat: Infinity,
      ease: "easeOut",
      repeatDelay: Math.random() * 3,
    }}
    sx={{
      position: "absolute",
      left: x,
      bottom: y,
      width: size,
      height: size,
      borderRadius: "50%",
      background: color,
      pointerEvents: "none",
      zIndex: 0,
    }}
  />
);

// ─── Floating ring ───────────────────────────────────────────────────────────
const FloatingRing = ({ sx, delay = 0 }) => (
  <MotionBox
    animate={{ rotate: 360 }}
    transition={{ duration: 18, repeat: Infinity, ease: "linear", delay }}
    sx={{
      position: "absolute",
      borderRadius: "50%",
      border: "1.5px solid",
      pointerEvents: "none",
      ...sx,
    }}
  />
);

// ─── Shimmer text component ──────────────────────────────────────────────────
const ShimmerText = ({ children, sx }) => (
  <Box
    component="span"
    sx={{
      background:
        "linear-gradient(90deg, #0288D1 0%, #38bdf8 40%, #7c3aed 70%, #0288D1 100%)",
      backgroundSize: "200% auto",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
      animation: "shimmerText 3s linear infinite",
      "@keyframes shimmerText": {
        "0%": { backgroundPosition: "0% center" },
        "100%": { backgroundPosition: "200% center" },
      },
      ...sx,
    }}
  >
    {children}
  </Box>
);

// ─── Animated border card ────────────────────────────────────────────────────
const GlowCard = ({ children }) => (
  <Box
    sx={{
      position: "relative",
      borderRadius: "28px",
      p: "1.5px",
      background:
        "linear-gradient(135deg, #0288D1 0%, #38bdf8 35%, #7c3aed 70%, #0288D1 100%)",
      backgroundSize: "300% 300%",
      animation: "borderRotate 5s ease infinite",
      "@keyframes borderRotate": {
        "0%": { backgroundPosition: "0% 50%" },
        "50%": { backgroundPosition: "100% 50%" },
        "100%": { backgroundPosition: "0% 50%" },
      },
      boxShadow:
        "0 0 60px rgba(2,136,209,0.25), 0 0 120px rgba(124,58,237,0.1)",
    }}
  >
    <Box
      sx={{
        borderRadius: "26.5px",
        background: "rgba(240,249,255,0.97)",
        backdropFilter: "blur(32px)",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {children}
    </Box>
  </Box>
);

// ─── Tilt card wrapper ───────────────────────────────────────────────────────
const TiltCard = ({ children }) => {
  const ref = useRef(null);
  const rotateX = useMotionValue(0);
  const rotateY = useMotionValue(0);
  const springX = useSpring(rotateX, { stiffness: 200, damping: 20 });
  const springY = useSpring(rotateY, { stiffness: 200, damping: 20 });

  const handleMouse = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    rotateY.set(((e.clientX - cx) / (rect.width / 2)) * 5);
    rotateX.set(-((e.clientY - cy) / (rect.height / 2)) * 5);
  };

  return (
    <MotionBox
      ref={ref}
      onMouseMove={handleMouse}
      onMouseLeave={() => {
        rotateX.set(0);
        rotateY.set(0);
      }}
      style={{
        rotateX: springX,
        rotateY: springY,
        transformStyle: "preserve-3d",
      }}
      sx={{ perspective: 1000, width: "100%", maxWidth: 440 }}
    >
      {children}
    </MotionBox>
  );
};

// ─── Particles data ──────────────────────────────────────────────────────────
const PARTICLES = Array.from({ length: 18 }, (_, i) => ({
  id: i,
  delay: Math.random() * 4,
  duration: 3 + Math.random() * 3,
  x: `${Math.random() * 100}%`,
  y: `${Math.random() * 30}%`,
  size: 3 + Math.random() * 5,
  color: ["#38bdf8", "#7c3aed", "#0288D1", "#06b6d4", "#a78bfa"][
    Math.floor(Math.random() * 5)
  ],
}));

// ─── Main component ──────────────────────────────────────────────────────────
const SendPasswordResetEmailPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [focused, setFocused] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Reset Password — LiteUpper";
  }, []);

  const handleBackClick = () => navigate(-1);

  const handleSendEmail = async () => {
    if (!email) {
      const msg = "Email is required.";
      setError(msg);
      toast.error(msg);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      const msg = "Please enter a valid email address.";
      setError(msg);
      toast.error(msg);
      return;
    }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API_URL}/send-reset-password/`, { email });
      setSuccess(true);
      setTimeout(() => navigate("/login"), 50000);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        "Failed to send email. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(145deg, #e0f7ff 0%, #f0f9ff 30%, #ede9fe 60%, #e0f2fe 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        px: 2,
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      {/* ── Google Font ─────────────────────────────────────────────── */}
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700;900&family=Syne:wght@700;800&display=swap');`}</style>

      {/* ── Vivid background blobs ───────────────────────────────────── */}
      {[
        {
          w: 560,
          h: 560,
          top: -160,
          left: -160,
          bg: "radial-gradient(circle, rgba(2,136,209,0.22) 0%, transparent 70%)",
        },
        {
          w: 400,
          h: 400,
          bottom: -120,
          right: -100,
          bg: "radial-gradient(circle, rgba(124,58,237,0.2) 0%, transparent 70%)",
        },
        {
          w: 300,
          h: 300,
          top: "38%",
          left: "52%",
          bg: "radial-gradient(circle, rgba(6,182,212,0.18) 0%, transparent 70%)",
        },
        {
          w: 200,
          h: 200,
          top: "15%",
          right: "8%",
          bg: "radial-gradient(circle, rgba(56,189,248,0.25) 0%, transparent 70%)",
        },
      ].map((o, i) => (
        <MotionBox
          key={i}
          animate={{ scale: [1, 1.08, 1], opacity: [0.8, 1, 0.8] }}
          transition={{
            duration: 6 + i * 2,
            repeat: Infinity,
            ease: "easeInOut",
            delay: i * 1.2,
          }}
          sx={{
            position: "absolute",
            borderRadius: "50%",
            filter: "blur(60px)",
            pointerEvents: "none",
            ...o,
          }}
        />
      ))}

      {/* ── Crisp dot grid ──────────────────────────────────────────── */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          opacity: 0.45,
          backgroundImage:
            "radial-gradient(circle, rgba(2,136,209,0.3) 1.2px, transparent 1.2px)",
          backgroundSize: "26px 26px",
        }}
      />

      {/* ── Floating rings ──────────────────────────────────────────── */}
      <FloatingRing
        delay={0}
        sx={{
          width: 520,
          height: 520,
          top: -200,
          left: -200,
          borderColor: "rgba(2,136,209,0.08)",
        }}
      />
      <FloatingRing
        delay={3}
        sx={{
          width: 340,
          height: 340,
          bottom: -100,
          right: -100,
          borderColor: "rgba(124,58,237,0.1)",
        }}
      />
      <FloatingRing
        delay={6}
        sx={{
          width: 180,
          height: 180,
          top: "25%",
          right: "12%",
          borderColor: "rgba(6,182,212,0.12)",
        }}
      />

      {/* ── Rising particles ────────────────────────────────────────── */}
      {PARTICLES.map((p) => (
        <Particle key={p.id} {...p} />
      ))}

      {/* ── Back button ─────────────────────────────────────────────── */}
      <MotionBox
        initial={{ opacity: 0, x: -24, scale: 0.85 }}
        animate={{ opacity: 1, x: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: EASE, delay: 0.15 }}
        sx={{ position: "fixed", top: 24, left: 24, zIndex: 20 }}
      >
        <MotionBox whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.92 }}>
          <IconButton
            onClick={handleBackClick}
            sx={{
              background: "rgba(255,255,255,0.85)",
              backdropFilter: "blur(16px)",
              border: "1.5px solid rgba(2,136,209,0.25)",
              color: "#0288D1",
              boxShadow: "0 4px 20px rgba(2,136,209,0.18)",
              "&:hover": {
                background: "rgba(2,136,209,0.12)",
                boxShadow: "0 6px 28px rgba(2,136,209,0.28)",
              },
              transition: "all 0.25s",
            }}
          >
            <FaArrowLeft size={15} />
          </IconButton>
        </MotionBox>
      </MotionBox>

      {/* ── Main card ───────────────────────────────────────────────── */}
      <TiltCard>
        <MotionBox
          initial={{ opacity: 0, y: 56, scale: 0.94, filter: "blur(12px)" }}
          animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
          transition={{ duration: 1.1, ease: EASE }}
        >
          <GlowCard>
            {/* Inner noise texture */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                borderRadius: "26.5px",
                zIndex: 0,
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.025'/%3E%3C/svg%3E\")",
                backgroundSize: "200px 200px",
                opacity: 0.6,
              }}
            />

            <Box
              sx={{ p: { xs: 3.5, sm: 4.5 }, position: "relative", zIndex: 1 }}
            >
              <AnimatePresence mode="wait">
                {/* ════════════════════════════════════
                     STATE A — Email form
                ════════════════════════════════════ */}
                {!success ? (
                  <MotionBox
                    key="form"
                    initial="hidden"
                    animate="show"
                    exit={{
                      opacity: 0,
                      y: -24,
                      filter: "blur(8px)",
                      transition: { duration: 0.4, ease: EASE },
                    }}
                    variants={{
                      hidden: {},
                      show: {
                        transition: {
                          staggerChildren: 0.09,
                          delayChildren: 0.15,
                        },
                      },
                    }}
                  >
                    {/* Logo */}
                    <MotionBox
                      variants={{
                        hidden: { opacity: 0, y: 20, filter: "blur(4px)" },
                        show: {
                          opacity: 1,
                          y: 0,
                          filter: "blur(0px)",
                          transition: { duration: 0.7, ease: EASE },
                        },
                      }}
                      sx={{ textAlign: "center", mb: 3 }}
                    >
                      <Link to="/">
                        <MotionBox
                          whileHover={{ scale: 1.06, rotate: -1 }}
                          whileTap={{ scale: 0.96 }}
                          sx={{ display: "inline-block" }}
                        >
                          <Box
                            component="img"
                            src={liteIcon}
                            alt="LiteUpper"
                            sx={{
                              height: 72,
                              mb: 2,
                              filter:
                                "drop-shadow(0 6px 20px rgba(2,136,209,0.35))",
                            }}
                          />
                        </MotionBox>
                      </Link>

                      {/* Pulsing icon badge */}
                      <MotionBox
                        animate={{
                          boxShadow: [
                            "0 0 0 0 rgba(2,136,209,0.4)",
                            "0 0 0 14px rgba(2,136,209,0)",
                            "0 0 0 0 rgba(2,136,209,0)",
                          ],
                        }}
                        transition={{
                          duration: 2.5,
                          repeat: Infinity,
                          ease: "easeOut",
                        }}
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: "50%",
                          mx: "auto",
                          mb: 2.5,
                          background:
                            "linear-gradient(135deg, #0288D1 0%, #38bdf8 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 8px 32px rgba(2,136,209,0.4)",
                        }}
                      >
                        <FaEnvelope size={24} color="#fff" />
                      </MotionBox>

                      <Chip
                        label="Password Reset"
                        size="small"
                        sx={{
                          mb: 1.5,
                          fontWeight: 700,
                          fontSize: "0.7rem",
                          background:
                            "linear-gradient(135deg, rgba(2,136,209,0.12), rgba(56,189,248,0.1))",
                          color: "#0277bd",
                          border: "1px solid rgba(2,136,209,0.3)",
                          letterSpacing: "0.6px",
                          textTransform: "uppercase",
                          fontFamily: "'Syne', sans-serif",
                        }}
                      />
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 900,
                          color: "#0c1a2e",
                          letterSpacing: "-0.8px",
                          lineHeight: 1.2,
                          mb: 0.8,
                          fontFamily: "'Syne', sans-serif",
                          fontSize: "1.65rem",
                        }}
                      >
                        Forgot your <ShimmerText>password?</ShimmerText>
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#475569",
                          maxWidth: 290,
                          mx: "auto",
                          lineHeight: 1.65,
                          fontSize: "0.875rem",
                        }}
                      >
                        No worries — drop your email and we'll fire a reset link
                        straight to your inbox.
                      </Typography>
                    </MotionBox>

                    {/* Email field */}
                    <MotionBox
                      variants={{
                        hidden: { opacity: 0, y: 20, filter: "blur(4px)" },
                        show: {
                          opacity: 1,
                          y: 0,
                          filter: "blur(0px)",
                          transition: { duration: 0.7, ease: EASE },
                        },
                      }}
                      sx={{ mb: 2.5 }}
                    >
                      <TextField
                        fullWidth
                        type="email"
                        placeholder="your@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value.trim())}
                        onFocus={() => setFocused(true)}
                        onBlur={() => setFocused(false)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <MotionBox
                                animate={{
                                  scale: focused ? 1.15 : 1,
                                  color: focused ? "#0288D1" : "#94a3b8",
                                }}
                                transition={SPRING}
                              >
                                <FaEnvelope
                                  size={15}
                                  color={focused ? "#0288D1" : "#94a3b8"}
                                />
                              </MotionBox>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 3,
                            background: focused
                              ? "rgba(2,136,209,0.05)"
                              : "rgba(255,255,255,0.85)",
                            backdropFilter: "blur(8px)",
                            transition: "all 0.3s",
                            color: "#0c1a2e",
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: "0.95rem",
                            "& fieldset": {
                              borderColor: focused
                                ? "#0288D1"
                                : "rgba(2,136,209,0.2)",
                              borderWidth: focused ? "2px" : "1.5px",
                              transition: "all 0.3s",
                            },
                            "&:hover fieldset": { borderColor: "#38bdf8" },
                            boxShadow: focused
                              ? "0 0 0 4px rgba(2,136,209,0.12), 0 4px 16px rgba(2,136,209,0.1)"
                              : "none",
                          },
                          "& input::placeholder": {
                            color: "#94a3b8",
                            opacity: 1,
                          },
                        }}
                      />
                      <AnimatePresence>
                        {error && (
                          <MotionBox
                            initial={{ opacity: 0, y: -6, height: 0 }}
                            animate={{ opacity: 1, y: 0, height: "auto" }}
                            exit={{ opacity: 0, y: -6, height: 0 }}
                            transition={{ duration: 0.3 }}
                            sx={{ mt: 1, px: 1 }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#dc2626",
                                fontWeight: 600,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              ⚠ {error}
                            </Typography>
                          </MotionBox>
                        )}
                      </AnimatePresence>
                    </MotionBox>

                    {/* Submit button */}
                    <MotionBox
                      variants={{
                        hidden: { opacity: 0, y: 20, filter: "blur(4px)" },
                        show: {
                          opacity: 1,
                          y: 0,
                          filter: "blur(0px)",
                          transition: { duration: 0.7, ease: EASE },
                        },
                      }}
                      sx={{ mb: 3 }}
                    >
                      <MotionBox
                        whileHover={
                          !loading && email ? { scale: 1.02, y: -2 } : {}
                        }
                        whileTap={!loading && email ? { scale: 0.97 } : {}}
                      >
                        <Button
                          fullWidth
                          variant="contained"
                          onClick={handleSendEmail}
                          disabled={loading || !email}
                          startIcon={
                            !loading && (
                              <MotionBox
                                animate={{ x: [0, 2, 0] }}
                                transition={{ duration: 1.5, repeat: Infinity }}
                              >
                                <FaPaperPlane size={14} />
                              </MotionBox>
                            )
                          }
                          sx={{
                            py: 1.75,
                            borderRadius: 3,
                            fontWeight: 800,
                            fontSize: "1rem",
                            textTransform: "none",
                            letterSpacing: "0.2px",
                            fontFamily: "'Syne', sans-serif",
                            background:
                              loading || !email
                                ? "rgba(2,136,209,0.45)"
                                : "linear-gradient(135deg, #0288D1 0%, #0ea5e9 50%, #38bdf8 100%)",
                            backgroundSize: "200% 200%",
                            animation:
                              !loading && email
                                ? "btnShimmer 3s ease infinite"
                                : "none",
                            "@keyframes btnShimmer": {
                              "0%": { backgroundPosition: "0% 50%" },
                              "50%": { backgroundPosition: "100% 50%" },
                              "100%": { backgroundPosition: "0% 50%" },
                            },
                            boxShadow:
                              loading || !email
                                ? "none"
                                : "0 8px 32px rgba(2,136,209,0.4), 0 2px 8px rgba(2,136,209,0.2)",
                            color: "#fff",
                            "&:disabled": {
                              background: "rgba(2,136,209,0.4)",
                              color: "rgba(255,255,255,0.7)",
                            },
                            transition: "all 0.3s",
                          }}
                        >
                          {loading ? (
                            <Stack
                              direction="row"
                              spacing={1.5}
                              alignItems="center"
                            >
                              <CircularProgress
                                size={18}
                                sx={{ color: "#fff" }}
                              />
                              <span>Sending…</span>
                            </Stack>
                          ) : (
                            "Send Reset Link"
                          )}
                        </Button>
                      </MotionBox>
                    </MotionBox>

                    {/* Back to login */}
                    <MotionBox
                      variants={{
                        hidden: { opacity: 0, y: 20, filter: "blur(4px)" },
                        show: {
                          opacity: 1,
                          y: 0,
                          filter: "blur(0px)",
                          transition: { duration: 0.7, ease: EASE },
                        },
                      }}
                      sx={{ textAlign: "center" }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#64748b",
                          fontFamily: "'DM Sans', sans-serif",
                        }}
                      >
                        Remembered it?{" "}
                        <MuiLink
                          component={Link}
                          to="/login"
                          underline="none"
                          sx={{
                            fontWeight: 800,
                            color: "#0288D1",
                            background:
                              "linear-gradient(90deg, #0288D1, #7c3aed)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                            "&:hover": { opacity: 0.8 },
                            transition: "opacity 0.2s",
                          }}
                        >
                          Back to sign in →
                        </MuiLink>
                      </Typography>
                    </MotionBox>
                  </MotionBox>
                ) : (
                  /* ════════════════════════════════════
                       STATE B — Success
                  ════════════════════════════════════ */
                  <MotionBox
                    key="success"
                    initial="hidden"
                    animate="show"
                    variants={{
                      hidden: {},
                      show: {
                        transition: {
                          staggerChildren: 0.1,
                          delayChildren: 0.1,
                        },
                      },
                    }}
                    sx={{ textAlign: "center", py: 2 }}
                  >
                    {/* Burst icon */}
                    <MotionBox
                      variants={{
                        hidden: {
                          opacity: 0,
                          scale: 0.5,
                          filter: "blur(12px)",
                        },
                        show: {
                          opacity: 1,
                          scale: 1,
                          filter: "blur(0px)",
                          transition: {
                            duration: 0.8,
                            ease: [0.34, 1.56, 0.64, 1],
                          },
                        },
                      }}
                      sx={{ mb: 3, position: "relative" }}
                    >
                      {/* Ring burst */}
                      {[1, 2, 3].map((i) => (
                        <MotionBox
                          key={i}
                          initial={{ opacity: 0.6, scale: 0.8 }}
                          animate={{ opacity: 0, scale: 2.2 }}
                          transition={{
                            duration: 1.5,
                            delay: i * 0.3,
                            repeat: Infinity,
                            ease: "easeOut",
                          }}
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%,-50%)",
                            width: 80,
                            height: 80,
                            borderRadius: "50%",
                            border: "2px solid rgba(5,150,105,0.4)",
                            pointerEvents: "none",
                          }}
                        />
                      ))}
                      <Box
                        sx={{
                          width: 88,
                          height: 88,
                          borderRadius: "50%",
                          mx: "auto",
                          background:
                            "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 12px 40px rgba(5,150,105,0.4)",
                          position: "relative",
                          zIndex: 1,
                        }}
                      >
                        <MotionBox
                          initial={{ pathLength: 0 }}
                          animate={{ pathLength: 1 }}
                          transition={{ duration: 0.6, delay: 0.3 }}
                        >
                          <FaCheckCircle size={38} color="#fff" />
                        </MotionBox>
                      </Box>
                    </MotionBox>

                    <MotionBox
                      variants={{
                        hidden: { opacity: 0, y: 20 },
                        show: {
                          opacity: 1,
                          y: 0,
                          transition: { duration: 0.6, ease: EASE },
                        },
                      }}
                      sx={{ mb: 1 }}
                    >
                      <Chip
                        label="✓ Email Sent"
                        size="small"
                        sx={{
                          mb: 2,
                          fontWeight: 700,
                          fontSize: "0.7rem",
                          background:
                            "linear-gradient(135deg, rgba(5,150,105,0.12), rgba(16,185,129,0.08))",
                          color: "#059669",
                          border: "1px solid rgba(5,150,105,0.3)",
                          letterSpacing: "0.5px",
                          textTransform: "uppercase",
                          fontFamily: "'Syne', sans-serif",
                        }}
                      />
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 900,
                          color: "#0c1a2e",
                          letterSpacing: "-0.8px",
                          mb: 1.5,
                          fontFamily: "'Syne', sans-serif",
                          fontSize: "1.65rem",
                        }}
                      >
                        Check your{" "}
                        <Box
                          component="span"
                          sx={{
                            background:
                              "linear-gradient(90deg, #059669, #10b981)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                          }}
                        >
                          inbox
                        </Box>
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#475569",
                          maxWidth: 300,
                          mx: "auto",
                          lineHeight: 1.7,
                        }}
                      >
                        A password reset link was sent to{" "}
                        <Box
                          component="span"
                          sx={{ fontWeight: 800, color: "#0288D1" }}
                        >
                          {email}
                        </Box>
                        . It should arrive any second.
                      </Typography>
                    </MotionBox>

                    {/* Info box */}
                    <MotionBox
                      variants={{
                        hidden: { opacity: 0, y: 20 },
                        show: {
                          opacity: 1,
                          y: 0,
                          transition: { duration: 0.6, ease: EASE },
                        },
                      }}
                      sx={{ mt: 3, mb: 2.5 }}
                    >
                      <Box
                        sx={{
                          px: 2.5,
                          py: 2,
                          borderRadius: 3,
                          background:
                            "linear-gradient(135deg, rgba(2,136,209,0.07), rgba(56,189,248,0.05))",
                          border: "1.5px solid rgba(2,136,209,0.15)",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#475569",
                            lineHeight: 1.7,
                            display: "block",
                          }}
                        >
                          📭 Didn't get it? Check your spam folder or{" "}
                          <Box
                            component="span"
                            onClick={() => setSuccess(false)}
                            sx={{
                              fontWeight: 800,
                              color: "#0288D1",
                              cursor: "pointer",
                              "&:hover": { textDecoration: "underline" },
                              transition: "color 0.2s",
                            }}
                          >
                            try a different email
                          </Box>
                          .
                        </Typography>
                      </Box>
                    </MotionBox>

                    <MotionBox
                      variants={{
                        hidden: { opacity: 0, y: 20 },
                        show: {
                          opacity: 1,
                          y: 0,
                          transition: { duration: 0.6, ease: EASE },
                        },
                      }}
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      <Button
                        fullWidth
                        variant="contained"
                        component={Link}
                        to="/login"
                        sx={{
                          py: 1.75,
                          borderRadius: 3,
                          fontWeight: 800,
                          fontSize: "1rem",
                          textTransform: "none",
                          letterSpacing: "0.2px",
                          fontFamily: "'Syne', sans-serif",
                          background:
                            "linear-gradient(135deg, #0288D1 0%, #0ea5e9 50%, #38bdf8 100%)",
                          backgroundSize: "200% 200%",
                          animation: "btnShimmer 3s ease infinite",
                          boxShadow: "0 8px 32px rgba(2,136,209,0.4)",
                          "&:hover": {
                            boxShadow: "0 12px 40px rgba(2,136,209,0.5)",
                          },
                          transition: "box-shadow 0.3s",
                        }}
                      >
                        Back to Sign In →
                      </Button>
                    </MotionBox>
                  </MotionBox>
                )}
              </AnimatePresence>
            </Box>
          </GlowCard>
        </MotionBox>
      </TiltCard>
    </Box>
  );
};

export default SendPasswordResetEmailPage;
